import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClickAwayListener, Input, SxProps, TextareaAutosize, Typography } from "@mui/material";
import styled from "styled-components";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { CSSProperties } from "@mui/styles";

export interface InlineInputProps {
    text: string;
    placeholder?: string;
    onEdit: (newValue: string) => void;
    fullWidth?: boolean;
    width?: string;
    multiLine?: boolean;
    readOnly?: boolean;
    small?: boolean;
    sx?: CSSProperties;
    sxTextField?: CSSProperties;
    sxLabel?: SxProps;
}

export const InheritLabel = styled(Typography)`
    && {
        border-bottom: 1px solid transparent;
        word-break: break-word;
        white-space: pre-wrap;
        min-width: 100px;

        font-style: normal;
        font-weight: 400;

        letter-spacing: 0em;
        text-align: left;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        &:hover {
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        }
    }
`;

export const EditInput = styled(Input)`
    && {
        font-size: inherit;
        min-width: 100px;

        line-height: inherit;

        .MuiInputBase-input {
            background: ${UIStyleConstants.BLUE_VERY_LIGHT_COLOR};
            border: none;
            border-bottom: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
            box-sizing: border-box;

            height: auto;
            padding: 6px 12px;
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

            //styleName: text/Inter Regular 12px;

            font-style: normal;
            font-weight: 400;
            letter-spacing: 0px;
            text-align: left;
        }
    }
`;

export const EditTextArea = styled(TextareaAutosize)`
    && {
        font-size: inherit;
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        box-sizing: border-box;
        border-radius: 4px;
        height: auto;
        width: 100%;

        font-style: normal;
        font-weight: 300;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        padding: 6px 12px;
    }
`;

export const InlineTextEdit = React.forwardRef<HTMLElement, InlineInputProps>(
    (
        {
            text,
            onEdit,
            placeholder,
            fullWidth,
            width,
            multiLine,
            readOnly,
            small,
            sx,
            sxTextField,
            sxLabel,
        }: InlineInputProps,
        refObject,
    ) => {
        const [editing, setEditing] = useState(false);
        const [editValue, setEditValue] = useState(text || "");

        let clickAway = useCallback(() => {
            let newValue = editValue.replace(/\n/g, "");
            !newValue ? setEditValue(newValue) : onEdit(editValue);
            setEditing(false);
        }, [editValue, onEdit]);

        useEffect(() => {
            if (text) setEditValue(text);
            else setEditValue("");
        }, [text]);
        const renderInput = useMemo(() => {
            return (
                <Typography component="div">
                    {multiLine ? (
                        <>
                            <EditTextArea
                                autoFocus
                                onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) =>
                                    event.currentTarget.setSelectionRange(
                                        event.currentTarget.value.length,
                                        event.currentTarget.value.length,
                                    )
                                }
                                value={editValue}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    setEditValue(event.target.value);
                                }}
                                onBlur={clickAway}
                                onKeyDown={(event: React.KeyboardEvent) => {
                                    if (event.key === "tab") {
                                        clickAway();
                                    }
                                }}
                                placeholder={placeholder}
                                style={{ width: width, overflow: "auto", ...(sxTextField || sx) }}
                                minRows="3"
                                data-testid="MultiInlineTextEditInput"
                            />
                        </>
                    ) : (
                        <EditInput
                            sx={sxTextField || sx}
                            value={editValue}
                            disableUnderline
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setEditValue(event.target.value);
                            }}
                            onBlur={clickAway}
                            onKeyDown={(event: React.KeyboardEvent) => {
                                if (event.key === "Enter" || event.key === "Escape") {
                                    clickAway();
                                }
                            }}
                            className="InlineEdit InlineEdit-input"
                            placeholder={placeholder}
                            autoFocus
                            data-testid="InlineTextEditInput"
                            fullWidth={fullWidth}
                            style={{ width: width }}
                        />
                    )}
                </Typography>
            );
        }, [clickAway, editValue, fullWidth, multiLine, placeholder, width]);

        let padding = "0px";
        if (!small) {
            padding = "6px 12px";
        }

        const renderLabel = () => {
            return (
                <InheritLabel
                    sx={sxLabel || sx}
                    className="InlineEdit InlineEdit-label"
                    data-testid="InlineTextEditLabel"
                    style={{ padding: padding }}
                    onClick={
                        !readOnly
                            ? (event: React.MouseEvent<HTMLLabelElement>) => {
                                event.stopPropagation();
                                setEditing(true);
                            }
                            : undefined
                    }
                    ref={refObject}
                >
                    {editValue || placeholder || "..."}
                </InheritLabel>
            );
        };

        return editing && !readOnly ? (
            <ClickAwayListener onClickAway={clickAway}>{renderInput}</ClickAwayListener>
        ) : (
            renderLabel()
        );
    },
);
